import React, { useContext, useState } from 'react'
import { useParams, Redirect } from 'react-router'
import base64 from 'base-64'
import constants from '../../../config/constants'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Form, Row, Col } from 'react-bootstrap'
import { inviteSignup, getProfile } from '../../../services/vendorService'
import { toast } from 'react-toastify'
import { friendlyMessageFor } from '../../../utility/errorHelper'
import { firebaseAuth } from '../../../services/firebase'
import { AppError } from '../../../config/enums'
import { VendorContext } from '../../../contexts/vendorContext'
import { AuthContext, UserType } from '../../../contexts/authContext'
import Layout from '../../common/layout/layout'

const schema = yup.object({
  password: yup
    .string()
    .min(
      constants.passwordMinLength,
      constants.inlineError.auth.invalidPassword
    )
    .required(constants.inlineError.fieldRequired.replace('%s', 'Password'))
})

function InviteSignup() {
  const authContext = useContext(AuthContext)
  const vendorContext = useContext(VendorContext)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (authContext?.userType !== UserType.vendor) {
    authContext?.setUserType(UserType.vendor)
  }

  let { email, token } = useParams()
  if (!email || !token) {
    toast.warn('Invalid invitation link')
    return <Redirect to="/vendor" />
  }

  email = base64.decode(email)
  token = base64.decode(token)

  const signup = (password: string) => {
    setIsSubmitting(true)
    inviteSignup(email as string, password, token as string)
      .then(() => {
        return firebaseAuth().signInWithEmailAndPassword(
          email as string,
          password
        )
      })
      .then((res) => {
        const userId = res?.user?.uid as string
        return getProfile(userId)
      })
      .then((profile) => {
        if (profile) {
          // case: user has a vendor profile
          // action: save profile to context and complete login
          console.info('Fetched vendor profile:', profile)
          vendorContext?.setProfile(profile)
          setIsSubmitting(false)
        } else {
          // case: user does not have a vendor profile!
          // action: signout and show an error message
          firebaseAuth().signOut()
          const message = friendlyMessageFor(
            AppError.unregisteredAccount as string
          ).replace('%s', 'vendor')
          toast.error(message, { autoClose: false })
          setIsSubmitting(false)
        }
      })
      .catch((err) => {
        console.error('Vendor invite signup error:', err)
        toast.error(friendlyMessageFor(err), { autoClose: false })
        setIsSubmitting(false)
      })
  }

  return (
    <Layout>
      <Row>
        <Col xs={12}>
          <h1 className="largeTitle">Welcome!</h1>
        </Col>
      </Row>
      <Row>
        <Col md={8} lg={5} className="mx-auto mt-5">
          <div className="box">
            <Row className="pb-3">
              <Col>
                <h2>Sign Up</h2>
              </Col>
            </Row>
            <Row>
              <Col>
                <Formik
                  validationSchema={schema}
                  onSubmit={(values) => {
                    signup(values.password)
                  }}
                  initialValues={{
                    password: ''
                  }}
                >
                  {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    errors,
                    touched
                  }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                      <Form.Group>
                        <Form.Label
                          className="mochi-sign-in-label"
                          htmlFor="signup-email"
                        >
                          <b>Email</b>
                        </Form.Label>
                        <Form.Control
                          id="signup-email"
                          type="email"
                          name="email"
                          value={email}
                          readOnly
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label
                          className="mochi-sign-in-label"
                          htmlFor="signup-password"
                        >
                          <b>Password</b>
                        </Form.Label>
                        <Form.Control
                          id="signup-password"
                          type="password"
                          name="password"
                          placeholder="Enter password"
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.password && !!errors.password}
                          isValid={touched.password && !errors.password}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.password}
                        </Form.Control.Feedback>
                      </Form.Group>
                      <div className="text-center mt-4">
                        <button
                          className="mochi-button orange"
                          type="submit"
                          data-testid="signup-submit-button"
                          disabled={isSubmitting}
                        >
                          <b>{isSubmitting ? 'Signing Up...' : 'Sign Up'}</b>
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </Layout>
  )
}

export default InviteSignup
